/**
 * 出庫 · **交收一掣**（P1-7；`decisions/005` §2 ＋ `decisions/018`）
 *
 * 產品負責人 2026-09-28：倉執完貨、貨離開之後**只撳一個掣**就收工 ——
 * 「the warehouse only need to click one final button that already collect or delivery」。
 *   · 自取（`self_pickup`）→ 掣文字「已收貨」
 *   · 送貨（`logistics`）→ 掣文字「已送貨」
 *   · 直銷場（`direct_sale`）→ 掣文字「已交收（場務）」
 * 交收日期一律**今日**（系統帶，唔問倉）；備註收埋落「＋加備註」（可選）。
 *
 * 去向**唔同**（睇類型，後端 `statusAfterDelivered(kind)` 決定）：
 *   · `delivery`（送貨／樣品）→ 送達即完（`closed`）
 *   · `direct_sale`（直銷場）→ 只到 `delivered`，**之後由 85 職員做場後結算**（`decisions/018`）
 * ⚠️ 未執貨（`sent`）唔會見到呢個掣：貨都未離開倉，冇得交收（後端一樣擋）。
 */
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import FormTextField from '@coeldery/form-text-field';
import { btnGhost, btnPrimary, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type DeliveryMode, type OutboundKind } from '../lib/api';

export interface OutboundDeliverFormProps {
  orderId: string;
  /** 睇類型決定提示（`direct_sale` 交收完仲要 85 職員做場後結算） */
  kind: OutboundKind;
  /** 睇取貨方式決定掣文字：自取 ＝ 已收貨／物流送貨 ＝ 已送貨（`decisions/018`） */
  mode: DeliveryMode;
  onDone: () => Promise<void>;
}

export default function OutboundDeliverForm({ orderId, kind, mode, onDone }: OutboundDeliverFormProps) {
  const { t } = useTranslation();
  /** 交收日期一律今日（系統帶）—— 倉唔需要入任何嘢 */
  const today = new Date().toISOString().slice(0, 10);
  const [note, setNote] = useState<string | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /** 一掣文字（`decisions/018`）：直銷場 ＝ 已交收／自取 ＝ 已收貨／送貨 ＝ 已送貨 */
  const label =
    kind === 'direct_sale'
      ? t('out.deliverSale')
      : mode === 'self_pickup'
        ? t('out.collectNow')
        : t('out.deliverNow');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!window.confirm(t('out.deliverOneTapAsk', { what: label }))) return;
    setBusy(true);
    setError(null);
    try {
      await api.deliverOutbound(orderId, { deliveredAt: today, note: note?.trim() || null });
      setDone(true);
      await onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{ border: '1px dashed var(--color-divider)', borderRadius: '12px', padding: '12px', marginTop: '10px' }}
    >
      <strong style={{ fontSize: '14px' }}>{t('out.deliverTitle')}</strong>
      <p style={hintStyle}>{kind === 'direct_sale' ? t('out.deliverHintSale') : t('out.deliverHintDelivery')}</p>

      {/* 一掣：≥48px（手機單手可撳；`ui-spec` §5 掣 ≥44px） */}
      <button
        type="submit"
        style={{ ...btnPrimary, width: '100%', marginTop: '8px', minHeight: '48px', fontSize: '17px' }}
        disabled={busy || done}
      >
        {busy ? t('common.saving') : done ? t('out.delivered') : label}
      </button>
      <p style={{ ...hintStyle, marginTop: '6px' }}>
        {t('out.deliveredAt')}：{today}
      </p>

      {showNote ? (
        <div style={{ marginTop: '6px' }}>
          <FormTextField id="f-ob-delivered-note" labelKey="out.deliverNote" value={note} onChange={setNote} />
        </div>
      ) : (
        <button type="button" style={{ ...btnGhost, marginTop: '6px' }} onClick={() => setShowNote(true)}>
          {t('out.deliverNoteOptional')}
        </button>
      )}

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
